/**
 * WHO-5743. Transport seam for the viewer-scoped Pro Buyer price fields.
 *
 * WHY NOT APOLLO
 * Same reason as `src/features/pro-buyer-application/data/pro-buyer-application-client.ts`:
 * codegen validates every `gql` template and `.graphql` document against the
 * LIVE production gateway schema. `Product.proBuyerPrice`,
 * `Thread.counterpartProBuyerPrice`, the cart item and order Pro Buyer
 * fields and `Address.companyName` are being built in a parallel gateway PR
 * and are deployed nowhere yet. One document naming them would fail codegen
 * and remove the Vercel preview of every open PR in this repo.
 *
 * So the operations are plain strings (no `gql` tag: graphql-tag-pluck would
 * extract a template) sent through `dashboardRequest`, and every reader is
 * fail-soft: a gateway without the fields, an error, a timeout or a signed-out
 * viewer all come back as "no Pro Buyer data", and nothing renders.
 *
 * Every existing query keeps selecting what it selects today, so the regular
 * price in SSR HTML, structured data and link previews is untouched.
 *
 * Once the gateway fields are on production, move these to `.graphql`
 * documents (fragments on the existing product, thread, cart and order
 * documents) and delete this file.
 */
import { Currency } from "@/Apollo/schema";
import { dashboardRequest } from "@/features/account-dashboard/data/dashboard-client";

export type ProBuyerMoney = { amount: number; currency: Currency };

/** `ProBuyerPrice { price: Price!, pct: Int! }` from the contract. */
export type ProBuyerPrice = { price: ProBuyerMoney; pct: number };

/** The Pro Buyer fields of one cart item. Null fields mean "not applied". */
export type ProBuyerCartLine = {
  id: string;
  listPrice: ProBuyerMoney | null;
  proBuyerPct: number | null;
  proBuyerDiscount: ProBuyerMoney | null;
  priceIncreased: boolean;
};

/** The Pro Buyer snapshot of one order (My purchases). */
export type ProBuyerOrderSnapshot = {
  id: string;
  listPrice: ProBuyerMoney | null;
  proBuyerPct: number | null;
  proBuyerDiscount: ProBuyerMoney | null;
  isProBuyerPurchase: boolean;
};

const PRICE_SELECTION = "{ amount currency }";
const PRO_BUYER_PRICE_SELECTION = `{ pct price ${PRICE_SELECTION} }`;

/** Max aliases per batched product request; one search page is 24 cards. */
export const PRODUCT_BATCH_SIZE = 24;

/* -- parsing: the payload is untrusted until it has the contract's shape --- */

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const parseMoney = (value: unknown): ProBuyerMoney | null => {
  if (!isRecord(value) || typeof value.amount !== "number") {
    return null;
  }

  if (typeof value.currency !== "string") {
    return null;
  }

  return { amount: value.amount, currency: value.currency as Currency };
};

export const parseProBuyerPrice = (value: unknown): ProBuyerPrice | null => {
  if (!isRecord(value) || typeof value.pct !== "number") {
    return null;
  }

  const price = parseMoney(value.price);

  return price ? { price, pct: value.pct } : null;
};

const parsePct = (value: unknown) => (typeof value === "number" ? value : null);

/* -- operations ------------------------------------------------------------ */

export const productProBuyerPricesOperation = (count: number) => {
  const variables = Array.from(
    { length: count },
    (_unused, index) => `$id${index}: UUID!`
  ).join(", ");
  const fields = Array.from(
    { length: count },
    (_unused, index) =>
      ` p${index}: product(id: $id${index}) { id proBuyerPrice ${PRO_BUYER_PRICE_SELECTION} }`
  ).join("");

  return `query ProBuyerProductPrices(${variables}) {${fields} }`;
};

export const THREAD_COUNTERPART_OPERATION =
  "query ProBuyerThreadCounterpart($id: UUID!) {" +
  ` thread(id: $id) { id counterpartProBuyerPrice ${PRO_BUYER_PRICE_SELECTION} }` +
  " }";

export const CART_PRO_BUYER_OPERATION =
  "query ProBuyerCartLines { cart { items {" +
  ` id listPrice ${PRICE_SELECTION} proBuyerPct` +
  ` proBuyerDiscount ${PRICE_SELECTION} priceIncreased` +
  " } } }";

export const orderGroupsProBuyerOperation = (count: number) => {
  const variables = Array.from(
    { length: count },
    (_unused, index) => `$id${index}: UUID!`
  ).join(", ");
  const fields = Array.from(
    { length: count },
    (_unused, index) =>
      ` g${index}: orderGroupById(id: $id${index}) { id orders {` +
      ` id listPrice ${PRICE_SELECTION} proBuyerPct` +
      ` proBuyerDiscount ${PRICE_SELECTION} isProBuyerPurchase } }`
  ).join("");

  return `query ProBuyerOrderGroups(${variables}) {${fields} }`;
};

/** WHO-5950: the same snapshot per order id, for the seller (Mijn verkopen). */
export const ordersProBuyerOperation = (count: number) => {
  const variables = Array.from(
    { length: count },
    (_unused, index) => `$id${index}: UUID!`
  ).join(", ");
  const fields = Array.from(
    { length: count },
    (_unused, index) =>
      ` o${index}: order(id: $id${index}) {` +
      ` id listPrice ${PRICE_SELECTION} proBuyerPct` +
      ` proBuyerDiscount ${PRICE_SELECTION} isProBuyerPurchase }`
  ).join("");

  return `query ProBuyerOrders(${variables}) {${fields} }`;
};

export const ADDRESS_COMPANY_NAMES_OPERATION =
  "query ProBuyerAddressCompanyNames { me { merchants { id addresses { id companyName } } } }";

export const SET_ADDRESS_COMPANY_NAME_OPERATION =
  "mutation ProBuyerSetAddressCompanyName($id: UUID!, $input: AddressInput!) {" +
  " updateAddress(id: $id, input: $input) { id companyName }" +
  " }";

const idVariables = (ids: readonly string[]) =>
  Object.fromEntries(ids.map((id, index) => [`id${index}`, id]));

/* -- readers (fail-soft) --------------------------------------------------- */

/**
 * Pro Buyer prices for up to `PRODUCT_BATCH_SIZE` products, keyed by id.
 * A product the gateway returns no price for is absent from the map.
 */
export const fetchProductProBuyerPrices = async (
  token: string | null,
  ids: readonly string[]
): Promise<Map<string, ProBuyerPrice>> => {
  const prices = new Map<string, ProBuyerPrice>();

  if (ids.length === 0) {
    return prices;
  }

  const result = await dashboardRequest<Record<string, unknown>>({
    operation: productProBuyerPricesOperation(ids.length),
    variables: idVariables(ids),
    token,
  });

  if (!result.ok) {
    return prices;
  }

  ids.forEach((id, index) => {
    const product = result.data[`p${index}`];
    const price = isRecord(product)
      ? parseProBuyerPrice(product.proBuyerPrice)
      : null;

    if (price) {
      prices.set(id, price);
    }
  });

  return prices;
};

/** Seller side of a chat: the counterpart's Pro Buyer price, or null. */
export const fetchThreadCounterpartProBuyerPrice = async (
  token: string | null,
  threadId: string
): Promise<ProBuyerPrice | null> => {
  const result = await dashboardRequest<{ thread?: unknown }>({
    operation: THREAD_COUNTERPART_OPERATION,
    variables: { id: threadId },
    token,
  });

  if (!result.ok || !isRecord(result.data.thread)) {
    return null;
  }

  return parseProBuyerPrice(result.data.thread.counterpartProBuyerPrice);
};

/** Pro Buyer fields per cart item id. Empty when the gateway has none. */
export const fetchCartProBuyerLines = async (
  token: string | null
): Promise<Map<string, ProBuyerCartLine>> => {
  const lines = new Map<string, ProBuyerCartLine>();
  const result = await dashboardRequest<{ cart?: unknown }>({
    operation: CART_PRO_BUYER_OPERATION,
    token,
  });

  if (!result.ok || !isRecord(result.data.cart)) {
    return lines;
  }

  const items = Array.isArray(result.data.cart.items)
    ? result.data.cart.items
    : [];

  for (const item of items) {
    if (!isRecord(item) || typeof item.id !== "string") {
      continue;
    }

    lines.set(item.id, {
      id: item.id,
      listPrice: parseMoney(item.listPrice),
      proBuyerPct: parsePct(item.proBuyerPct),
      proBuyerDiscount: parseMoney(item.proBuyerDiscount),
      priceIncreased: item.priceIncreased === true,
    });
  }

  return lines;
};

/** Pro Buyer order snapshots per order id, for the given order groups. */
const parseOrderSnapshot = (order: unknown): ProBuyerOrderSnapshot | null => {
  if (!isRecord(order) || typeof order.id !== "string") {
    return null;
  }

  return {
    id: order.id,
    listPrice: parseMoney(order.listPrice),
    proBuyerPct: parsePct(order.proBuyerPct),
    proBuyerDiscount: parseMoney(order.proBuyerDiscount),
    isProBuyerPurchase: order.isProBuyerPurchase === true,
  };
};

/**
 * WHO-5950. Seller side: Pro Buyer snapshot per order id. The testing gateway
 * (next.whoppah.com) does not have these fields yet, so this stays a
 * fail-soft string operation: no gateway field, an error or a timeout all
 * come back as an empty map and nothing renders.
 */
export const fetchOrdersProBuyer = async (
  token: string | null,
  orderIds: readonly string[]
): Promise<Map<string, ProBuyerOrderSnapshot>> => {
  const snapshots = new Map<string, ProBuyerOrderSnapshot>();

  if (orderIds.length === 0) {
    return snapshots;
  }

  const result = await dashboardRequest<Record<string, unknown>>({
    operation: ordersProBuyerOperation(orderIds.length),
    variables: idVariables(orderIds),
    token,
  });

  if (!result.ok) {
    return snapshots;
  }

  orderIds.forEach((_orderId, index) => {
    const snapshot = parseOrderSnapshot(result.data[`o${index}`]);

    if (snapshot) {
      snapshots.set(snapshot.id, snapshot);
    }
  });

  return snapshots;
};

export const fetchOrderGroupsProBuyer = async (
  token: string | null,
  groupIds: readonly string[]
): Promise<Map<string, ProBuyerOrderSnapshot>> => {
  const snapshots = new Map<string, ProBuyerOrderSnapshot>();

  if (groupIds.length === 0) {
    return snapshots;
  }

  const result = await dashboardRequest<Record<string, unknown>>({
    operation: orderGroupsProBuyerOperation(groupIds.length),
    variables: idVariables(groupIds),
    token,
  });

  if (!result.ok) {
    return snapshots;
  }

  groupIds.forEach((_groupId, index) => {
    const group = result.data[`g${index}`];
    const orders =
      isRecord(group) && Array.isArray(group.orders) ? group.orders : [];

    for (const order of orders) {
      if (!isRecord(order) || typeof order.id !== "string") {
        continue;
      }

      snapshots.set(order.id, {
        id: order.id,
        listPrice: parseMoney(order.listPrice),
        proBuyerPct: parsePct(order.proBuyerPct),
        proBuyerDiscount: parseMoney(order.proBuyerDiscount),
        isProBuyerPurchase: order.isProBuyerPurchase === true,
      });
    }
  });

  return snapshots;
};

/**
 * Company name per address id for the viewer's merchants. Null when the
 * gateway has no `Address.companyName` yet: the address pop-up then hides
 * the field instead of offering one it cannot save.
 */
export const fetchAddressCompanyNames = async (
  token: string | null
): Promise<Map<string, string | null> | null> => {
  const result = await dashboardRequest<{ me?: unknown }>({
    operation: ADDRESS_COMPANY_NAMES_OPERATION,
    token,
  });

  if (!result.ok || !isRecord(result.data.me)) {
    return null;
  }

  const names = new Map<string, string | null>();
  const merchants = Array.isArray(result.data.me.merchants)
    ? result.data.me.merchants
    : [];

  for (const merchant of merchants) {
    const addresses =
      isRecord(merchant) && Array.isArray(merchant.addresses)
        ? merchant.addresses
        : [];

    for (const address of addresses) {
      if (isRecord(address) && typeof address.id === "string") {
        names.set(
          address.id,
          typeof address.companyName === "string" ? address.companyName : null
        );
      }
    }
  }

  return names;
};

export type SetAddressCompanyNameInput = {
  addressId: string;
  /** The full address the pop-up just saved: `updateAddress` takes the whole input. */
  address: {
    merchant_id: string;
    country: string;
    line1: string;
    line2?: string | null;
    postal_code: string;
    city: string;
  };
  companyName: string | null;
};

/** Stores the optional company name on an address the pop-up just saved. */
export const setAddressCompanyName = async (
  token: string | null,
  { addressId, address, companyName }: SetAddressCompanyNameInput
): Promise<boolean> => {
  const result = await dashboardRequest<unknown>({
    operation: SET_ADDRESS_COMPANY_NAME_OPERATION,
    variables: { id: addressId, input: { ...address, companyName } },
    token,
  });

  return result.ok;
};
